import { StrictMode, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Link, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StackProvider, StackHandler, StackTheme } from '@stackframe/react';
import { ArrowLeft } from 'lucide-react';
import { stackApp } from './lib/stack.ts';
import { trpc, createTrpcClient } from './lib/trpc.ts';
import { App, initialActiveTeam } from './App.tsx';
import { setActiveStackTeamId } from './lib/activeTenant.ts';
import { ToastProvider } from './components/Toast.tsx';
import { ThemeProvider } from './components/useTheme.tsx';
import { ConfirmProvider } from './components/ConfirmDialog.tsx';
import './styles.css';

function AuthBackLink() {
  return (
    <Link
      to="/"
      aria-label="Back to home"
      className="auth-back"
    >
      <ArrowLeft size={16} />
      <span>Back</span>
    </Link>
  );
}

function Root() {
  const [activeTeam, setTeam] = useState<string | null>(initialActiveTeam());
  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() => createTrpcClient(() => activeTeam));

  function handleChangeTeam(id: string | null) {
    setTeam(id);
    setActiveStackTeamId(id);
  }

  return (
    <StackProvider app={stackApp}>
      <StackTheme>
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
            <ToastProvider>
            <ConfirmProvider>
              <Routes>
                <Route
                  path="/handler/*"
                  element={
                    <>
                      <AuthBackLink />
                      <StackHandler app={stackApp} fullPage />
                    </>
                  }
                />
                <Route
                  path="*"
                  element={
                    <App activeStackTeamId={activeTeam} setActiveStackTeamId={handleChangeTeam} />
                  }
                />
              </Routes>
            </ConfirmProvider>
            </ToastProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </trpc.Provider>
      </StackTheme>
    </StackProvider>
  );
}

const root = document.getElementById('root');
if (!root) throw new Error('#root missing from index.html');

createRoot(root).render(
  <StrictMode>
    <BrowserRouter>
      <Root />
    </BrowserRouter>
  </StrictMode>,
);
